import type { Association, Selection, SelectionStatus, StoredFixture, TeamSheetPlayer, TeamSheetResult } from "../shared/contracts";

export function normalisePlayerName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-GB");
}

export function playerIsSelected(players: ReadonlyArray<string | TeamSheetPlayer>, pupilName: string): boolean {
  const expected = normalisePlayerName(pupilName);
  return players.some((player) => normalisePlayerName(typeof player === "string" ? player : player.name) === expected);
}

export function isWithinUpcomingSheetWindow(date: string, today = new Date(), days = 2): boolean {
  const fixtureTime = Date.parse(`${date}T12:00:00Z`);
  const todayTime = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12);
  const windowSize = days * 24 * 60 * 60 * 1000;
  return Number.isFinite(fixtureTime) && fixtureTime >= todayTime && fixtureTime <= todayTime + windowSize;
}

export function isUpcomingFixture(date: string, today = new Date()): boolean {
  const fixtureTime = Date.parse(`${date}T12:00:00Z`);
  const todayTime = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12);
  return Number.isFinite(fixtureTime) && fixtureTime >= todayTime;
}

export function fixtureIdsToRemove(
  fixtures: ReadonlyArray<Pick<StoredFixture, "id" | "teamId" | "date">>,
  remoteFixtureIdsByTeam: ReadonlyMap<string, ReadonlySet<string>>,
  today = new Date(),
): string[] {
  return fixtures
    .filter((fixture) => {
      if (!isUpcomingFixture(fixture.date, today)) return true;
      const remoteIds = remoteFixtureIdsByTeam.get(fixture.teamId);
      return remoteIds ? !remoteIds.has(fixture.id) : false;
    })
    .map((fixture) => fixture.id);
}

export function statusFor(args: {
  association: Association;
  sheet: TeamSheetResult | null;
  selection?: Selection;
  checkFailed?: boolean;
}): SelectionStatus {
  // A transient failure is displayed separately and must not erase a previously known result.
  if (args.selection) return args.selection.selected ? "confirmed" : "not_selected";
  if (args.checkFailed) return "unable_to_check";
  if (args.sheet?.status === "published") {
    return "not_selected";
  }
  if (args.sheet?.status === "incorrect_password") return "unable_to_check";
  return args.association.confidence === "known" ? "awaiting_team_sheet" : "possible";
}

export function recordAppearance(association: Association, confirmedAt: string): Association {
  return {
    ...association,
    confidence: "known",
    lastConfirmedAt: confirmedAt,
    appearanceCount: association.appearanceCount + 1,
  };
}
